import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import client from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ExternalLink, Sparkles, Crown } from "lucide-react";

const ArticleDetail = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (signalTimeout = 90000) => {
    setLoading(true);
    setError(null);
    setA(null);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), signalTimeout);
    try {
      const r = await client.get(`/articles/${slug}`, { signal: ctrl.signal });
      // Article meta always returned; body may be null when audience-gated.
      if (r.data?.gated) {
        setA(r.data);
      } else if (!r.data?.body_md || r.data.body_md.length < 500) {
        setError("O artigo está a ser gerado. Tenta novamente em alguns segundos.");
      } else {
        setA(r.data);
      }
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        setError("Demorou demasiado. Tenta novamente.");
      } else if (e?.response?.status === 404) {
        setError("Artigo não encontrado.");
      } else {
        setError("Falha ao carregar.");
      }
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="article-detail-page">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <Link to="/artigos" className="inline-flex items-center gap-2 text-[#A29F9B] hover:text-[#C5A880] mb-8"
          data-testid="article-back">
          <ArrowLeft className="w-4 h-4" /> Voltar aos artigos
        </Link>

        {loading && (
          <div className="text-[#A29F9B]" data-testid="article-loading">
            <div className="inline-flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#C5A880] animate-pulse" />
              A preparar o artigo... (a primeira leitura pode demorar 20-40s — gerado por IA)
            </div>
          </div>
        )}
        {error && !loading && (
          <div className="bg-[#7A2021]/10 border border-[#7A2021]/40 rounded-2xl p-6 text-center"
            data-testid="article-error">
            <p className="text-[#E37B7B] mb-4">{error}</p>
            <button onClick={() => load()} className="btn-primary inline-flex items-center gap-2"
              data-testid="article-retry">
              <Sparkles className="w-4 h-4" /> Tentar novamente
            </button>
          </div>
        )}

        {a && (
          <article>
            <span className="label-uppercase">{a.category}</span>
            <h1 className="font-display text-4xl md:text-5xl mt-3 mb-4 leading-tight">{a.title}</h1>
            <p className="text-[#A29F9B] text-lg mb-10 italic leading-relaxed">{a.summary}</p>

            {a.gated === "members" && (
              <div className="bg-gradient-to-br from-[#C5A880]/10 to-[#161920] border border-[#C5A880]/30 rounded-2xl p-8 text-center"
                   data-testid="article-gate-members">
                <Crown className="w-7 h-7 text-[#C5A880] mx-auto mb-3" />
                <h3 className="font-display text-2xl text-[#F5F2EE] mb-2">Conteúdo para membros</h3>
                <p className="text-sm text-[#A29F9B] mb-6 max-w-md mx-auto">
                  Este artigo está reservado a utilizadores com conta. Cria a tua
                  (gratuita) ou inicia sessão para o leres por completo.
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link to="/login" className="btn-secondary inline-flex items-center gap-1.5"
                        data-testid="article-gate-login">Entrar</Link>
                  <Link to="/register" className="btn-primary inline-flex items-center gap-1.5"
                        data-testid="article-gate-register">Criar conta grátis</Link>
                </div>
              </div>
            )}

            {a.gated === "premium" && (
              <div className="bg-gradient-to-br from-[#C5A880]/15 to-[#161920] border border-[#C5A880]/40 rounded-2xl p-8 text-center"
                   data-testid="article-gate-premium">
                <Crown className="w-7 h-7 text-[#C5A880] mx-auto mb-3" />
                <h3 className="font-display text-2xl text-[#F5F2EE] mb-2">Artigo exclusivo Premium</h3>
                <p className="text-sm text-[#A29F9B] mb-6 max-w-md mx-auto">
                  Este artigo é só para subscritores Premium. Subscreve para
                  acederes ao corpo completo, à validação IA e à pesquisa pública.
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  {!user && (
                    <Link to="/login" className="btn-secondary inline-flex items-center gap-1.5"
                          data-testid="article-gate-login">Entrar</Link>
                  )}
                  <Link to="/premium" className="btn-primary inline-flex items-center gap-1.5"
                        data-testid="article-gate-premium-cta">
                    <Sparkles className="w-4 h-4" /> Subscrever Premium
                  </Link>
                </div>
              </div>
            )}

            {!a.gated && (
              <div className="prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(a.body_md), {
                  ADD_ATTR: ["target", "rel"],
                }) }}
                data-testid="article-body" />
            )}

            {/* CTAs at the end of every article */}
            <div className="grid md:grid-cols-2 gap-4 mt-16 pt-10 border-t border-white/5">
              <a href="https://ihavethepower.net/loja/cursos-em-video/workshop-o-que-ninguem-te-ensinou-sobre-relacoes-video-online-com-adelino-cunha/"
                target="_blank" rel="noopener noreferrer"
                className="p-6 rounded-2xl bg-[#161920] border border-[#C5A880]/30 hover:border-[#C5A880] transition-colors block"
                data-testid="article-cta-workshop">
                <span className="label-uppercase">Workshop</span>
                <h3 className="font-display text-lg mt-2 mb-2 text-[#F5F2EE]">"O que ninguém te ensinou sobre Relações"</h3>
                <p className="text-xs text-[#A29F9B] mb-3">Workshop online com Adelino Cunha. Vê quando quiseres.</p>
                <span className="inline-flex items-center gap-1 text-xs text-[#C5A880]">Aceder <ExternalLink className="w-3 h-3" /></span>
              </a>
              <a href="https://ihavethepower.net/loja/coaching/life-coaching-com-coach-ihtp/"
                target="_blank" rel="noopener noreferrer"
                className="p-6 rounded-2xl bg-[#161920] border border-[#C5A880]/30 hover:border-[#C5A880] transition-colors block"
                data-testid="article-cta-coaching">
                <span className="label-uppercase">Coaching 1-a-1</span>
                <h3 className="font-display text-lg mt-2 mb-2 text-[#F5F2EE]">Acompanhamento personalizado</h3>
                <p className="text-xs text-[#A29F9B] mb-3">Sessões individuais para validares o(a) candidato(a) ou preparares o 1º encontro.</p>
                <span className="inline-flex items-center gap-1 text-xs text-[#C5A880]">Saber mais <ExternalLink className="w-3 h-3" /></span>
              </a>
            </div>

            {!user?.is_premium && (
              <Link to="/premium" className="block mt-6 p-6 rounded-2xl bg-gradient-to-r from-[#C5A880]/15 to-[#161920] border border-[#C5A880]/30 hover:border-[#C5A880] transition-colors"
                data-testid="article-cta-premium">
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-[#C5A880]" />
                  <div className="flex-1">
                    <h3 className="font-display text-lg text-[#F5F2EE]">Aplicar este conhecimento na Soulmatch</h3>
                    <p className="text-xs text-[#A29F9B] mt-1">A versão Premium dá-te validação IA, pesquisa pública e perguntas para o 1º encontro.</p>
                  </div>
                  <Sparkles className="w-5 h-5 text-[#C5A880]" />
                </div>
              </Link>
            )}
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ArticleDetail;
